import { toast, type ToastOptions, type Id } from "react-toastify";
import BaseToast from "@/shared/components/ui/Toast/BaseToast";
import {
  type ToastData,
  type ToastVariant,
  TOAST_CONFIG,
} from "@/shared/components/ui/Toast/toast.types";
import CloseButton from "@/shared/components/ui/Toast/CloseButton";

interface ShowToastOptions extends Partial<ToastData> {
  duration?: number;
  toastId?: string;
}

class ToastService {
  private static instance: ToastService;
  private readonly defaultOptions: ToastOptions = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
  };

  private constructor() {}

  public static getInstance(): ToastService {
    if (!ToastService.instance) {
      ToastService.instance = new ToastService();
    }
    return ToastService.instance;
  }

  private show(
    variant: ToastVariant,
    title: string,
    content: string,
    options?: ShowToastOptions,
  ): Id {
    const config = TOAST_CONFIG[variant];
    return toast(BaseToast, {
      ...this.defaultOptions,
      autoClose: options?.duration ?? this.defaultOptions.autoClose,
      toastId: options?.toastId,
      data: {
        title,
        content,
        iconName: options?.iconName ?? config.iconName,
        variant,
      },
      style: {
        backgroundColor: config.backgroundColor,
        color: "#ffffff",
      },
      closeButton: CloseButton,
    });
  }
  // Public method for each toast type
  public success(
    title: string,
    content: string,
    options?: ShowToastOptions,
  ): Id {
    return this.show("success", title, content, options);
  }
  public error(title: string, content: string, options?: ShowToastOptions): Id {
    return this.show("error", title, content, options);
  }
  public warning(
    title: string,
    content: string,
    options?: ShowToastOptions,
  ): Id {
    return this.show("warning", title, content, options);
  }

  public info(title: string, content: string, options?: ShowToastOptions): Id {
    return this.show("info", title, content, options);
  }

  public danger(
    title: string,
    content: string,
    options?: ShowToastOptions,
  ): Id {
    return this.show("danger", title, content, options);
  }

  // Utility methods
  public dismiss(toastId?: Id): void {
    if (toastId) {
      toast.dismiss(toastId);
    } else {
      toast.dismiss();
    }
  }
  public update(
    toastId: Id,
    variant: ToastVariant,
    title: string,
    content: string,
  ): void {
    const config = TOAST_CONFIG[variant];

    toast.update(toastId, {
      render: BaseToast,
      data: {
        title,
        content,
        iconName: config.iconName,
        variant,
      },
      style: {
        backgroundColor: config.backgroundColor,
        color: "#1a1a2e",
      },
    });
  }
  // Promise based toast
  public promise<T>(
    promise: Promise<T>,
    messages: {
      pending: { title: string; content: string };
      success: { title: string; content: string };
      error: { title: string; content: string };
    },
  ): Promise<T> {
    const toastId = this.info(messages.pending.title, messages.pending.content);
    return promise
      .then((result) => {
        this.update(
          toastId,
          "success",
          messages.success.title,
          messages.success.content,
        );
        return result;
      })
      .catch((error) => {
        this.update(
          toastId,
          "error",
          messages.error.title,
          messages.error.content,
        );
        throw error;
      });
  }
}
export const toastService: ToastService = ToastService.getInstance();
